import isArray from 'lodash/isArray';
import debounce from 'lodash/debounce';
import merge from 'lodash/merge';

// 地图 JSAPI / 对外服务接口 Key https://appkey-map.sankuai.com/
export const MT_MAP_KEY = '7189f84d-5039-4679-8b5e-1c3d29b69b6b';
// 地图对内服务接口 Key https://lbsmap.sankuai.com/console/key/app
export const MT_MAP_INTERNAL_KEY = 'md045d0796494a1db06666b12915317w';
// h5 地图
export const MT_MAP_API = `https://webapi-map.meituan.com/h5maps?v=1.12.0&key=${MT_MAP_KEY}&callback=_init_mt_map`;
// 矢量地图
export const MT_MAP_API_BETA = v =>
  `https://webapi-map.meituan.com/maps?v=${v}&key=${MT_MAP_KEY}&callback=_init_mt_map`;
// 地图对外服务接口
export const MT_MAP_SERVICE = {
  SEARCH_NEARBY: 'https://maf.meituan.com/search',
  GEO: 'https://maf.meituan.com/geo',
  REGEO: 'https://maf.meituan.com/regeo',
};
// 地图对内服务接口
export const MT_MAP_INTERNAL_SERVICE = {
  // 行政区域列表 https://km.sankuai.com/page/1070314981
  DIVISION: 'https://lbsapi.meituan.com/v1/search/division',
  // 经纬度查询行政区域 https://km.sankuai.com/page/1307675662
  SEARCH_DISTRICT: 'https://lbsapi.meituan.com/v1/search/district',
  // 关键字搜索 https://km.sankuai.com/page/1072540929
  SEARCH_TEXT: 'https://lbsapi.meituan.com/v1/search/text',
  // 附近POI搜索
  SEARCH_NEARBY: 'https://lbsapi.meituan.com/v1/search/nearby',
  GEO: 'https://lbsapi.meituan.com/v1/location/geo',
  REGEO: 'https://lbsapi.meituan.com/v1/location/regeo',
};

export function fetchMapService($api, serviceName, params) {
  return $api
    .get(MT_MAP_INTERNAL_SERVICE[serviceName], {
      params: {
        key: MT_MAP_INTERNAL_KEY,
        ...params,
      },
      __apiBusinessCode__: ['^500|310|305$'],
    })
    .then(resp => {
      if (resp?.data) return resp.data;
      return resp;
    });
}

export function fetchDivision($api, params) {
  return fetchMapService($api, 'DIVISION', params).then(
    response => response?.districts ?? response?.treeJson ?? []
  );
}

export function fetchSearchDistrict($api, params) {
  return fetchMapService($api, 'SEARCH_DISTRICT', params).then(
    response => response?.districts ?? []
  );
}

export function fetchSearchText($api, params) {
  return fetchMapService($api, 'SEARCH_TEXT', params).then(response => response ?? []);
}

export function fetchSearchNearby($api, params) {
  return fetchMapService($api, 'SEARCH_NEARBY', params).then(response => response?.pois ?? []);
}

export function fetchRegeo($api, lng, lat) {
  return fetchMapService($api, 'REGEO', {
    location: `${lng},${lat}`,
  }).then(response => {
    if (response?.status === 200 && response?.regeocode?.[0]?.formatted_address) {
      return response?.regeocode?.[0]?.formatted_address;
    }
    return '';
  });
}

export function fetchGeoCode($api, address, cityName) {
  return fetchMapService($api, 'GEO', {
    address,
    city: cityName || null,
  }).then(response => {
    const geoCodeResult = response?.geocodes?.[0];
    if (
      geoCodeResult?.location &&
      // GC 结果位置可信度
      geoCodeResult.confidence >= 10 &&
      // GC 搜索词解析可信度
      geoCodeResult.comprehension >= 50
    ) {
      const { location, formatted_address } = response?.geocodes?.[0];
      const [lng, lat] = location.split(',');
      return {
        lng: +lng,
        lat: +lat,
        address: formatted_address,
      };
    }
    return null;
  });
}

let callbackQueue = [];

export function initMtMap(options = {}) {
  const opt = merge(
    {
      beta: true,
      version: '1.12.0',
    },
    options
  );

  window._init_mt_map = () => {
    callbackQueue.forEach(callback => callback());
    callbackQueue = [];
  };

  if (typeof opt.callback === 'function') {
    callbackQueue.push(opt.callback);
  }

  if (window.mtdpMap) {
    window._init_mt_map();
    return;
  }
  // if (window._init_mt_map) return;
  let scriptEl = document.createElement('script');
  scriptEl.src = opt.beta ? MT_MAP_API_BETA(opt.version) : MT_MAP_API;
  scriptEl.src += '&plugin=PanControl,ZoomControl,GeoLocateControl,Circle';
  scriptEl.crossOrigin = 'anonymous';
  document.body.appendChild(scriptEl);
}

export default class MtMap {
  /**
   * @param {number[]} opt.center 中心点 [lng, lat]
   * @param {string|HTMLElement} opt.el 地图元素
   * @param {number} opt.zoom 初始放大层级
   * @param { dragend: Function } opt.event
   * @param {boolean} opt.beta 是否矢量地图
   * @param {boolean} opt.enableGeoLocate 是否添加定位组件
   * @param opt.GeoLocateControlOptions Object 定位组件配置
   * @param {boolean} opt.enableZoomControl 是否添加缩放组件
   * @param {boolean} opt.touchZoomCenter 是否按照中心点缩放
   * @param {string} opt.coordinateCheckApi String 坐标校验api
   * @param {boolean} opt.enableRegeo 是否在拖动之后返回逆地理编码结果
   * @param {boolean} opt.enableSearchNearby
   * @param {boolean} opt.enableScaleControl 是否添加比例尺
   * @param {Function} opt.created 初始化钩子
   * @param opt.$api Object
   * @param opt.$env Object
   * @param opt.extraMapOption Boolean 添加额外的地图配置
   * @param opt.overseasFlag Boolean 是否支持境外
   */
  constructor(opt) {
    this.$api = opt.$api;
    this.$env = opt.$env || {};
    this.mapInstance = null;
    this.container = opt.el;
    this.center = opt.center;
    this.zoom = opt.zoom || 16;
    this.version = opt.version || '1.12.0';
    this.coordinateCheckApi = opt.coordinateCheckApi;
    this._created = opt.created;
    this._events = opt.event;
    this._plugins = opt.plugins || [];
    this._status = 'init';
    this.queue = [];
    this.beta = opt.beta;
    this.geoLocateControl = null;
    this.geoLocateSceneToken = opt.sceneToken;
    this.enableRegeo = opt.enableRegeo;
    this.enableSearchNearby = opt.enableSearchNearby;
    this.nearbyConfig = opt.nearbyConfig ?? {};
    this.nearbyPois = [];
    this.staticNearbyPois = [];
    this.nearbyPoiMarkers = null;
    this.locateMarker = null;
    this.instantLocating = false;
    this.destroyed = false;
    this.fallbackLocateOnce = opt.fallbackLocateOnce ?? false;
    this.userPosition = {
      lng: null,
      lat: null,
    };
    this.lastCenter = null;
    this.errorHandler = opt.customErrorHandler || (() => {});

    this.debounceSearchNearby = debounce(this.searchNearby, 500);
    this.initMap(opt);
  }
  reportError(error, opt, params) {
    this.errorHandler(error, opt, params);
  }
  bridgeHandleError(error, methodName, params) {
    this.errorHandler(error, { methodName }, params);
  }
  initMap(opt) {
    // 适配前后两个页面同时需要mtmap，但一个页面添加控件另一个页面没有的特殊情况，默认加载
    // if (opt.enableGeoLocate) {
    //   this._plugins.push('GeoLocateControl');
    // }
    initMtMap({
      beta: this.beta,
      plugins: this._plugins,
      version: this.version,
      callback: () => {
        const {
          staticMap = false,
          newMapStyle = false,
          enableGeoLocate = false,
          enableZoomControl = false,
          enableScaleControl = false,
        } = opt;
        const { mtdpMap } = window;
        const staticConfig = {};
        const operationConfigKeys = [
          'draggable',
          'enableDblclickZoom',
          'enableKeyboard',
          'fadeAnimation',
          'inertialDragging',
          'scrollwheel',
          'zoomAnimation',
          'touchZoomCenter', // 是否仅按照中心点缩放
        ];
        operationConfigKeys.forEach(key => {
          staticConfig[key] = opt[key] ?? !staticMap;
        });
        this.mapInstance = new mtdpMap.Map({
          container: this.container,
          center: this.center,
          zoom: this.zoom,
          oversea: opt.overseasFlag,
          scaleControl: {
            visible: enableScaleControl,
            position: mtdpMap.ANCHOR_BOTTOM_RIGHT,
          },
          touchZoomCenter: true,
          bootstrap: newMapStyle
            ? 'sankuai://tile/style?{key}&{aid}&id=tuanapp_buxing1.json'
            : mtdpMap.MAPSTYLE_NORMAL,
          zoomControl: enableZoomControl
            ? {
                position: mtdpMap.ANCHOR_TOP_RIGHT,
                offset: [50, 0],
              }
            : false,
          ...staticConfig,
          ...(opt.extraMapOption ?? {}),
        });
        // 地图加载完成之后，初始化地图附加元素
        this.mapInstance.on('load', () => {
          this._status = 'loaded';
          typeof this._created === 'function' && this._created();
          let task;
          // eslint-disable-next-line no-cond-assign
          while ((task = this.queue.shift())) {
            task();
          }
          // 加载完底图立即触发更新会导致严重卡顿
          setTimeout(() => {
            this.handleDragEnd(null, 'initial');
          }, 1500);
        });
        if (opt.plugins.includes('PanControl')) {
          const panControl = new mtdpMap.PanControl({ position: mtdpMap.ANCHOR_TOP_RIGHT });
          this.mapInstance.addControl(panControl);
        }
        // 地图内置 KNB 定位模块，只支持单次定位
        if (this.beta && enableGeoLocate) {
          // 定位按钮样式
          this.geoLocateControl = new mtdpMap.GeoLocateControl({
            position: mtdpMap.ANCHOR_BOTTOM_LEFT,
            ...opt.GeoLocateControlOptions,
          });
          this.mapInstance.addControl(this.geoLocateControl);
          // 定位完成之后需要触发一次更新
          this.geoLocateControl.on('complete', e => {
            if (e?.data?.position) {
              this.handleDragEnd(e?.data?.position, 'locate');
            } else {
              // 由于地图改变中心点有一定延迟，所以 setTimeout
              setTimeout(() => {
                this.handleDragEnd(null, 'locate');
              }, 300);
            }
          });
        }
        if (this._events?.dragend) {
          // 手动防抖
          this.mapInstance.on('zoomend', e => {
            this.handleDragEnd(null, 'gesture');
          });
          this.mapInstance.on('moveend', e => {
            this.handleDragEnd(null, 'gesture');
          });
        }
        if (typeof this._events?.zoomend === 'function') {
          this.mapInstance.on('zoomend', event => {
            this._events.zoomend(event);
          });
        }
        if (typeof this._events?.zoomstart === 'function') {
          this.mapInstance.on('zoomstart', event => {
            this._events.zoomstart(event);
          });
        }
      },
    });
  }
  handleDragEnd(center, source = 'gesture') {
    if (!(typeof this._events?.dragend === 'function')) return;
    if (!center) {
      center = this.mapInstance.getCenter();
    }
    const expEqual = (a, b) => a.toFixed(6) === b.toFixed(6);
    if (
      !this.lastCenter ||
      !expEqual(this.lastCenter.lng, center.lng) ||
      !expEqual(this.lastCenter.lat, center.lat)
    ) {
      this.lastCenter = center;
      this.handleDragCheck(center, source);
    }
  }
  async handleDragCheck(center, source) {
    let address = '';
    let crossRoadCheck = 1;
    let inWaterCheck = 1;

    const apiRequests = [];
    if (this.coordinateCheckApi) {
      const coordinateCheckRequest = this.$api
        .get(this.coordinateCheckApi, {
          params: {
            lng: center.lng,
            lat: center.lat,
          },
        })
        .catch(() => ({
          data: {
            crossRoadCheck: 1,
            inWaterCheck: 1,
          },
        }))
        .then(({ data }) => {
          crossRoadCheck = data?.data?.crossRoadCheck ?? 1;
          inWaterCheck = data?.data?.inWaterCheck ?? 1;
        });
      apiRequests.push(coordinateCheckRequest);
    }
    if (this.enableRegeo) {
      const rgcRequest = fetchRegeo(this.$api, center.lng, center.lat)
        .catch(() => null)
        .then(result => {
          if (result) address = result;
        });
      apiRequests.push(rgcRequest);
    }
    if (this.enableSearchNearby) {
      apiRequests.push(this.debounceSearchNearby(center));
    }
    await Promise.all(apiRequests);
    this._events.dragend({
      center,
      address,
      crossRoadCheck,
      inWaterCheck,
      isGesture: source === 'gesture',
      source,
    });
  }
  async geoCode(address, cityName) {
    const location = await fetchGeoCode(this.$api, address, cityName);
    if (location) {
      this.panTo([location.lng, location.lat], 'search');
    }
    return location;
  }
  mark(markOpt) {
    try {
      return new window.mtdpMap.Marker({
        map: this.mapInstance,
        ...markOpt,
      });
    } catch (e) {
      this.reportError(e, {}, { level: 'warn' });
    }
  }
  infoWindow(option) {
    return new window.mtdpMap.InfoWindow({
      anchor: 'bottom-center',
      enableCloseOnClick: false,
      closeIcon: false,
      map: this.mapInstance,
      ...option,
    });
  }
  panTo(position, source = 'default') {
    const fn = () => {
      this.mapInstance.panTo(position, false);
      this.handleDragEnd(
        {
          lng: position[0],
          lat: position[1],
        },
        source
      );
    };
    if (this.mapInstance) {
      fn();
    } else {
      this.queue.push(fn);
    }
  }
  destroy() {
    this.destroyed = true;
    if (this.locateMarker) {
      this.locateMarker.destroy();
      this.locateMarker = null;
    }
    if (this.instantLocating) {
      window.KNB.stopLocating({
        sceneToken: this.geoLocateSceneToken,
      });
    }
    if (this.mapInstance) {
      this.mapInstance.destroy();
    }
    this._status = 'destroy';
    if (this.instantLocating) {
      window.KNB.stopLocating({
        sceneToken: this.geoLocateSceneToken,
        fail: err => {
          this.bridgeHandleError(err, 'stopLocating', { level: 'warn' });
        },
      });
      this.instantLocating = false;
    }
  }
  centerDistance(point) {
    const center = this.mapInstance.getCenter();
    return window.mtdpMap.GeoMath.lnglatDistance(point, center);
  }
  updateLocateMarker(userPosition) {
    if (this.destroyed) return;
    const { lng, lat } = userPosition || this.userPosition;
    const locateIcon = document.createElement('div');
    locateIcon.style.boxSizing = 'content-box';
    locateIcon.style.backgroundColor = this.instantLocating ? '#1DA1F2' : '#B3B3B3';
    locateIcon.style.width = '16px';
    locateIcon.style.height = '16px';
    locateIcon.style.boxShadow = '0 0 2px rgba(0, 0, 0, 0.25)';
    locateIcon.style.border = '2px solid #fff';
    locateIcon.style.borderRadius = '50%';
    locateIcon.style.animation = 'mtmap-user-location-dot-pulse 2s infinite';
    if (!this.locateMarker) {
      this.locateMarker = this.mark({
        position: [lng, lat],
        content: locateIcon,
      });
    } else {
      // 地图被销毁之后，不能再调用 moveTo
      if (!this.locateMarker.getMap()) return;
      this.locateMarker.moveTo([lng, lat]).catch(e => {
        this.reportError(e, { hasMap: !!this.locateMarker.getMap() }, { level: 'warn' });
      });
      this.locateMarker.setContent(locateIcon);
    }
  }
  panToLocate(isNative = true, silent = false, callback) {
    const handleLocate = ({ lng, lat }) => {
      this.userPosition.lng = lng;
      this.userPosition.lat = lat;
      this.updateLocateMarker({ lng, lat });
      if (typeof callback === 'function') {
        callback(this.userPosition);
      }
      if (!silent) {
        this.panTo([lng, lat], 'locate');
      }
    };
    if (isNative) {
      window.KNB.getLocation({
        type: 'gcj02',
        sceneToken: this.geoLocateSceneToken,
        success: handleLocate,
        fail: err => {
          this.bridgeHandleError(err, 'getLocation');
        },
      });
    } else {
      new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          position => {
            const { coords } = position;
            if (coords && coords.latitude && coords.longitude) {
              const { latitude, longitude } = coords;
              resolve({
                lat: latitude,
                lng: longitude,
              });
            }
          },
          e => {
            reject(e);
          }
        );
      })
        .then(handleLocate)
        .catch(e => {
          this.bridgeHandleError(e, 'getLocation');
        });
    }
  }
  instantLocate(isNative = true) {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        position => {
          const { coords } = position;
          if (coords && coords.latitude && coords.longitude) {
            const { latitude, longitude } = coords;
            this.instantLocating = true;
            resolve({
              lat: latitude,
              lng: longitude,
            });
          }
        },
        e => {
          reject(e);
        }
      );
    })
      .then(({ lng, lat }) => {
        if (!lng || !lat) return;
        this.userPosition.lng = lng;
        this.userPosition.lat = lat;
        this.updateLocateMarker({ lng, lat });
        return () => {
          this.instantLocating = false;
          return Promise.resolve();
        };
      })
      .catch(err => {
        this.bridgeHandleError(err, 'getLocation');
        throw err;
      });
  }
  // 在地图上绘制参照点
  async renderNearby() {
    const poiData = this.staticNearbyPois.concat(this.nearbyPois);
    if (poiData.length) {
      // 过滤掉和其他点相距太近的点（4m）
      // let nearbyPois = poiData;
      // let i = 0;
      // while (i < nearbyPois.length) {
      //   const poi = nearbyPois[i];
      //   const tooClose = nearbyPois.some(otherPoi => {
      //     if (poi.name === otherPoi.name) return false;
      //
      //     const { location } = poi;
      //     const { location: otherLocation } = otherPoi;
      //     return (
      //       window.mtdpMap.GeoMath.lnglatDistance(
      //         location.split(',').map(n => +n),
      //         otherLocation.split(',').map(n => +n)
      //       ) < 4
      //     );
      //   });
      //   if (tooClose) {
      //     nearbyPois.splice(i, 1);
      //   } else {
      //     i += 1;
      //   }
      // }
      // 用 GeoJSONLayer 绘制这些点，使他们与底图上已有的点碰撞
      const geoData = {
        type: 'FeatureCollection',
        features: poiData.map(poi => {
          const { location, name } = poi;
          const [plng, plat] = location.split(',');
          return {
            type: 'Feature',
            geometry: {
              type: 'Point',
              text: name,
              coordinates: [plng, plat],
            },
            properties: {
              name,
            },
          };
        }),
      };
      if (this.nearbyPoiMarkers) {
        this.nearbyPoiMarkers.remove();
      }

      const nearbyMarkerStyle = this.$env.isMT
        ? {
            icon: 'https://p0.meituan.net/ingee/d0fedafd7c4b8976ec38844eea9f837c2314.png',
          }
        : {
            icon: 'https://p0.meituan.net/ingee/fe851a47215ff2721c8b2de90b0fa2292517.png',
          };

      this.nearbyPoiMarkers = new window.mtdpMap.GeoJSONLayer({
        data: geoData,
        style: {
          type: 'point',
          antialias: true,
          icon: {
            name: 'nearby',
            url: nearbyMarkerStyle.icon,
            size: 0.5,
            'ignore-placement': false,
          },
          text: {
            order: 300,
            field: '{name}',
            size: 13,
            offset: [0, 1.5],
            'rotation-alignment': 'viewport',
            color: '#000000',
            'halo-width': 1,
            'halo-color': '#fff',
            opacity: 1,
            'allow-overlap': false,
            'ignore-placement': false,
          },
        },
      });
      await this.nearbyPoiMarkers.appendTo(this.mapInstance);
    }
  }
  async appendStaticNearby({ name, lng, lat }) {
    this.staticNearbyPois.push({
      name,
      location: `${lng},${lat}`,
    });
    await this.renderNearby();
  }
  clearStaticNearby() {
    this.staticNearbyPois = [];
  }
  async searchNearby() {
    const { lng, lat } = this.mapInstance.getCenter();
    this.nearbyPois = await fetchSearchNearby(this.$api, {
      location: `${lng},${lat}`,
      radius: 200,
      pageNo: 1,
      pagesize: 10,
      advancedfilter: 'point_tag,eq,1',
      ...this.nearbyConfig,
    }).catch(() => []);
    await this.renderNearby();
  }
  createCenterMarker(draggable = true) {
    if (this.centerMarker) {
      this.centerMarker.destroy();
    }
    this.centerMarker = new window.mtdpMap.Marker({
      map: this.mapInstance,
      position: this.mapInstance.getCenter(),
      draggable,
    });
  }
}
